/**
 * StepUpDialog — modal that runs the request → enter-code → verify
 * step-up flow for any sensitive operation.
 *
 * Drives the matching server-side machinery:
 *   - `identity.requestStepUpOtp({action, channel})` → sends OTP
 *   - `identity.verifyStepUpOtp({challengeId, code, action})` → stamps
 *     the calling session's evidence so `stepUpProcedureFor(action)`
 *     on the server lets the follow-up mutation through.
 *
 * Do NOT mount this directly from feature code. Use the
 * `useStepUpGate(action)` hook instead — it owns the open/close
 * state, wraps your mutation thunk, and ensures the dialog is
 * mounted from one place per feature so the UX stays consistent.
 *
 * @see use-step-up-gate.ts — the public hook
 * @see packages/api-contract/src/step-up-actions.ts — action registry
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, ShieldCheck, X } from "lucide-react";
import {
  Alert,
  Button,
  DialogRoot,
  DialogContent,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@flint/ui";
import type { StepUpAction } from "@flint/api-contract";
import { trpc } from "../../lib/trpc.ts";

export interface StepUpDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  action: StepUpAction;
  /** Operator-facing copy: "Approve 3 bills · ₹78,650". Drives the header. */
  title: string;
  /** Sub-line: what the operator is about to authorise. */
  description: string;
  /**
   * Called once the OTP is verified — the parent then invokes the
   * gated mutation. The dialog closes itself on success.
   */
  onVerified: () => void;
  /** SMS by default; the operator can flip to WhatsApp inside the dialog. */
  defaultChannel?: "sms" | "whatsapp";
}

export function StepUpDialog({
  open,
  onOpenChange,
  action,
  title,
  description,
  onVerified,
  defaultChannel = "sms",
}: StepUpDialogProps) {
  const [channel, setChannel] = useState<"sms" | "whatsapp">(defaultChannel);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const lastAttempted = useRef<string | null>(null);

  const requestOtp = trpc.identity.requestStepUpOtp.useMutation();
  const verifyOtp = trpc.identity.verifyStepUpOtp.useMutation();

  // Issue a fresh challenge whenever the dialog opens (or the operator
  // flips channel). Closing the dialog clears local state so the next
  // open is a fresh slate.
  useEffect(() => {
    if (!open) {
      setChallengeId(null);
      setCode("");
      setError(null);
      lastAttempted.current = null;
      return;
    }
    let cancelled = false;
    setError(null);
    setCode("");
    setChallengeId(null);
    requestOtp
      .mutateAsync({ action, channel })
      .then((result: { challengeId: string }) => {
        if (cancelled) return;
        setChallengeId(result.challengeId);
      })
      .catch((err: { message?: string }) => {
        if (cancelled) return;
        setError(err.message ?? "Could not send code.");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutation ref stable
  }, [open, channel, action]);

  // Auto-submit at 6 digits — same pattern as `JoinOtpStep`. The retry-
  // loop guard prevents firing on the same code twice when React's
  // strict mode double-invokes the effect.
  useEffect(() => {
    if (
      open &&
      challengeId &&
      code.length === 6 &&
      code !== lastAttempted.current &&
      !verifyOtp.isPending
    ) {
      lastAttempted.current = code;
      verifyOtp
        .mutateAsync({ challengeId, code, action })
        .then(() => {
          // Do NOT close the dialog here. The hook (useStepUpGate)
          // closes it AFTER the wrapped thunk runs. If we close first
          // the hook's `handleOpenChange` resolves the pending promise
          // with `undefined` before `handleVerified` reads the ref,
          // and the thunk never fires.
          onVerified();
        })
        .catch(
          (err: {
            message?: string;
            data?: { code?: string; cause?: { attemptsRemaining?: number } };
          }) => {
            // Reset the field so the operator can retype after a wrong code.
            setCode("");
            lastAttempted.current = null;
            const remaining = err.data?.cause?.attemptsRemaining;
            if (typeof remaining === "number") {
              setError(`Wrong code · ${remaining} attempt${remaining === 1 ? "" : "s"} left.`);
            } else {
              setError(err.message ?? "Verification failed.");
            }
          },
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutation ref stable
  }, [code, challengeId, open, action]);

  const resend = () => {
    setCode("");
    setError(null);
    lastAttempted.current = null;
    setChallengeId(null);
    requestOtp
      .mutateAsync({ action, channel })
      .then((result: { challengeId: string }) => setChallengeId(result.challengeId))
      .catch((err: { message?: string }) => setError(err.message ?? "Could not send code."));
  };

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <div className="flex flex-col gap-4">
          <header className="flex items-start gap-3">
            <span
              aria-hidden
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-warn-50 text-warn"
            >
              <ShieldCheck size={16} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold text-ink m-0">{title}</h2>
              <p className="mt-1 text-xs text-muted leading-snug">{description}</p>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Cancel"
              className="grid h-7 w-7 shrink-0 place-items-center rounded-sm text-muted hover:bg-bg hover:text-ink"
            >
              <X size={14} />
            </button>
          </header>

          <div className="flex flex-col gap-3">
            <div className="text-xs text-muted">
              We sent a 6-digit code via{" "}
              <strong className="text-ink-2">{channel === "whatsapp" ? "WhatsApp" : "SMS"}</strong>{" "}
              to your registered phone. Expires in 5 minutes.
            </div>

            <InputOTP
              maxLength={6}
              autoComplete="one-time-code"
              inputMode="numeric"
              value={code}
              onChange={(next: string) => {
                setCode(next);
                if (error) setError(null);
              }}
              disabled={!challengeId || verifyOtp.isPending}
              autoFocus
            >
              <InputOTPGroup>
                {Array.from({ length: 6 }, (_, i) => (
                  <InputOTPSlot key={i} index={i} />
                ))}
              </InputOTPGroup>
            </InputOTP>

            {requestOtp.isPending && !challengeId ? (
              <p className="inline-flex items-center gap-2 text-xs text-muted">
                <Loader2 size={12} className="animate-spin" />
                Sending code…
              </p>
            ) : null}

            {verifyOtp.isPending ? (
              <p className="inline-flex items-center gap-2 text-xs text-muted">
                <Loader2 size={12} className="animate-spin" />
                Verifying…
              </p>
            ) : null}

            {error ? <Alert intent="bad" title="Couldn't verify" description={error} /> : null}
          </div>

          <footer className="flex items-center justify-between gap-2 pt-2 border-t border-line">
            <div className="flex items-center gap-3 text-xs">
              <button
                type="button"
                onClick={resend}
                disabled={requestOtp.isPending}
                className="text-sky-700 font-semibold hover:underline disabled:opacity-50"
              >
                Resend code
              </button>
              <span aria-hidden className="text-line-2">
                ·
              </span>
              <button
                type="button"
                onClick={() => setChannel(channel === "sms" ? "whatsapp" : "sms")}
                disabled={requestOtp.isPending}
                className="text-sky-700 font-semibold hover:underline disabled:opacity-50"
              >
                Use {channel === "sms" ? "WhatsApp" : "SMS"} instead
              </button>
            </div>
            <Button type="button" intent="ghost" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </footer>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}
